import {DatePicker, Form, Input, InputNumber, Select, Spin, Switch} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import moment from 'moment';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import v2 from '../v2/instrumentV2.module.scss';
import {FormCard, spans, Hint} from '../v2/FormCard';
import {InstrumentActions} from '../v2/InstrumentActions';
import {IPO_DETAILS_MOCK} from '../v2/devPrefill';
import {useRemainingFields} from '../v2/useRemainingFields';
import {groupThousands, parseThousands} from '../v2/numberFormat';
import {handleIpoFieldState} from 'redux/store/assetClass';
import {GET_IPO_BY_ID} from 'graphql/queries/assetManagement';
import {UPDATE_IPO} from 'graphql/mutations/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {ipoStatuses, NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {createIpoStepTwoRoute} from './routes';
import {formatEnumType} from 'utils/helper';
import {handleUpdateIpo} from '../actions';
import {
  collectTentativeDates,
  DATE_FIELDS,
  SHARED_ROW,
  tentativeDatesToForm,
  tentativeField,
} from './ipoTentativeDates';

/**
 * IPO offer terms.
 *
 * The seven dates below are the whole subscription timeline — `offering.timeline`
 * is projected from them rather than stored, so there is no timeline to edit
 * (§2). The preview at the bottom shows what investors will see.
 */
export const IpoDetails = () => {
  const [form] = Form.useForm();
  const {assetId, id, lang} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const draft = useSelector(state => state?.assetClass?.ipo) || {};
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [updateIpo, {loading: loadingUpdate}] = useMutation(UPDATE_IPO);
  const {data, refetch, loading} = useQuery(GET_IPO_BY_ID, {
    // The language versions table links here with the variant's language in
    // the URL; without passing it the read always came back in the base
    // language and the form silently showed the wrong translation.
    variables: {getFinancialInstrumentId: id, language: lang},
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [remaining, onValuesChange] = useRemainingFields(
    form,
    [
      'ticker',
      'offerPrice',
      'totalSharesOnOffer',
      'subscriptionStartDate',
      'subscriptionEndDate',
    ],
    data,
  );

  const applyValues = source => {
    if (!source) return;
    const dates = Object.fromEntries(
      DATE_FIELDS.map(([key]) => [
        key,
        source[key] ? moment(source[key]) : null,
      ]),
    );
    // Read the stored list into one switch per date, so the form can post the
    // complete list back on save.
    const tentative = tentativeDatesToForm(source.tentativeDates);
    form.setFieldsValue({
      ticker: source.ticker,
      isin: source.isin,
      offerPrice: source.offerPrice,
      priceRangeLow: source.priceRangeLow,
      priceRangeHigh: source.priceRangeHigh,
      totalSharesOnOffer: source.totalSharesOnOffer,
      dabaAllocationShares: source.dabaAllocationShares ?? null,
      ipoStatus: source.ipoStatus,
      exclusiveOnDaba: source.exclusiveOnDaba,
      fullAllocationLikely: source.fullAllocationLikely,
      highlights: source.highlights?.map(highlight => ({highlight})) ?? [],
      ...dates,
      ...tentative,
    });
  };

  useEffect(() => {
    applyValues(data?.getFinancialInstrument?.data);
  }, [data?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (!id && draft?.ticker) applyValues(draft);
  }, [draft]);

  const buildPayload = values => {
    const {ticker, isin, highlights, ...ipoFields} = values;
    // The per-date switches are form-only; the service takes one enum list.
    DATE_FIELDS.forEach(([, , enumValue]) => {
      delete ipoFields[tentativeField(enumValue)];
    });
    return {
      ...ipoFields,
      tentativeDates: collectTentativeDates(values),
      dabaAllocationShares: values.dabaAllocationShares ?? null,
      highlights: highlights?.map?.(h => h?.highlight).filter(Boolean) ?? [],
      // ticker and isin are collected here but belong to the base input.
      baseFinancialInstrumentInput: {ticker, isin},
    };
  };

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  const record = data?.getFinancialInstrument?.data;

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>IPO details</h2>
        <p>
          Offer terms and the subscription calendar. Investors see a timeline
          projected from these dates — edit the dates, not the timeline.
        </p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        onFinish={values => {
          if (id) {
            setOpenUpdateModal(true);
            setUpdatedValues(buildPayload(values));
          } else {
            const {baseFinancialInstrumentInput, ...flat} =
              buildPayload(values);
            dispatch(
              handleIpoFieldState({
                ...flat,
                ...baseFinancialInstrumentInput,
              }),
            );
            navigate(createIpoStepTwoRoute({param: assetId, name: 'profile'}));
          }
        }}>
        <FormCard
          title="Identifier"
          description="How this offer is quoted once it lists">
          <div className={spans.half}>
            <Form.Item
              name="ticker"
              label="Ticker"
              rules={[{required: true, message: 'Ticker is required'}]}>
              <Input placeholder="ACREN" />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="isin" label="ISIN">
              <Input />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Offer economics"
          description="What is on offer and at what price">
          <div className={spans.third}>
            <Form.Item
              name="offerPrice"
              label="Offer price"
              rules={[{required: true, message: 'Offer price is required'}]}>
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="priceRangeLow" label="Price range low">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item name="priceRangeHigh" label="Price range high">
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="totalSharesOnOffer"
              label="Total shares on offer"
              rules={[
                {required: true, message: 'Shares on offer is required'},
              ]}>
              <InputNumber formatter={groupThousands} parser={parseThousands} />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="dabaAllocationShares"
              label="Daba allocation (shares)"
              tooltip={{
                title:
                  'Shares allocated to Daba for subscription progress. Enter a positive whole number, or leave blank to use the total offer.',
                trigger: ['hover', 'focus', 'click'],
                icon: (
                  <button
                    type="button"
                    aria-label="About Daba allocation"
                    style={{background: 'none', border: 0, padding: 0}}>
                    <InfoCircleOutlined aria-hidden="true" />
                  </button>
                ),
              }}
              rules={[
                {
                  type: 'integer',
                  min: 1,
                  max: Number.MAX_SAFE_INTEGER,
                  message:
                    'Enter a whole number from 1 to 9,007,199,254,740,991.',
                },
              ]}>
              <InputNumber
                aria-label="Daba allocation (shares)"
                step={1}
                formatter={groupThousands}
                parser={parseThousands}
              />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="ipoStatus" label="IPO status">
              <Select placeholder="Select a status">
                {ipoStatuses.map(status => (
                  <Select.Option key={status.value} value={status.value}>
                    {status.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Hint>
              Stored, and never advances on its own — treat it as a manual
              override and trust the dates below.
            </Hint>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="exclusiveOnDaba"
              label="Exclusive on Daba"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="fullAllocationLikely"
              label="Full allocation likely"
              valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Subscription calendar"
          description="These seven dates project the timeline shown to investors. Mark any of them tentative to show it as provisional.">
          {DATE_FIELDS.map(([name, label, enumValue]) => {
            // Once the IPO lists, the app shows the real listing date and
            // reports that step as settled whatever the flag says. The switch
            // is disabled rather than hidden so its stored value stays
            // visible — it simply stops having an effect.
            const listingSettled =
              enumValue === 'EXPECTED_LISTING' &&
              record?.ipoStatus === 'LISTED';

            return (
              <div className={spans.half} key={name}>
                {/* The toggle rides in the field's label rather than under
                    the picker: it is a property of this date, and stacked
                    below it read as a separate field. */}
                <Form.Item
                  className={v2.fullWidthLabel}
                  name={name}
                  label={
                    <span className={v2.dateLabel}>
                      <span>{label}</span>
                      <span className={v2.dateLabelToggle}>
                        <Form.Item
                          name={tentativeField(enumValue)}
                          valuePropName="checked"
                          noStyle>
                          <Switch size="small" disabled={listingSettled} />
                        </Form.Item>
                        <span className={v2.tentativeLabel}>
                          {listingSettled ? 'Tentative (listed)' : 'Tentative'}
                        </span>
                      </span>
                    </span>
                  }
                  tooltip={
                    listingSettled
                      ? 'This IPO has listed, so the app shows the real listing date and ignores this toggle.'
                      : SHARED_ROW.includes(enumValue)
                      ? 'Allocation and refunds are one row in the app. Flagging either makes that whole row read tentative.'
                      : 'Marks this date provisional for investors.'
                  }
                  rules={[
                    {
                      required: name.startsWith('subscription'),
                      message: 'This date is required',
                    },
                  ]}>
                  <DatePicker
                    className={v2.fullWidth}
                    showTime={{defaultValue: moment('00:00', 'HH:mm:ss')}}
                    format="DD MMM YYYY, HH:mm"
                  />
                </Form.Item>
              </div>
            );
          })}
        </FormCard>

        {record?.offering?.timeline?.length > 0 && (
          <FormCard
            title="Timeline preview"
            description="Projected from the dates above — read only">
            <div className={spans.full}>
              <ul className={v2.timeline}>
                {record.offering.timeline.map(entry => (
                  <li key={entry.key || entry.label}>
                    <span className={v2.timelineLabel}>{entry.label}</span>
                    <span className={v2.timelineDate}>
                      {entry.date
                        ? moment(entry.date).format('DD MMM YYYY')
                        : '—'}
                    </span>
                    <span
                      className={`${v2.chip} ${
                        entry.status === 'COMPLETE' || entry.status === 'PAID'
                          ? v2.chipOk
                          : entry.status === 'CURRENT' ||
                            entry.status === 'NEXT'
                          ? v2.chipBrand
                          : v2.chipMute
                      }`}>
                      {entry.status ? formatEnumType(entry.status) : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </FormCard>
        )}

        <FormCard
          title="Highlights"
          description="Short selling points shown on the offer page">
          <Form.List name="highlights">
            {(fields, {add, remove}) => (
              <div className={v2.rows}>
                {fields.map(({key, name}) => (
                  <div className={v2.repeatRow} key={key}>
                    <div className={spans.full}>
                      <Form.Item
                        name={[name, 'highlight']}
                        rules={[{required: true, message: 'Required'}]}
                        label={`Highlight ${name + 1}`}>
                        <Input placeholder="Backed by a 12-year concession" />
                      </Form.Item>
                    </div>
                    <div className={v2.rowDelete}>
                      <span onClick={() => remove(name)}>Remove</span>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={add} className={v2.addRow}>
                  <span>＋ Add highlight</span>
                </button>
              </div>
            )}
          </Form.List>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => {
            form.setFieldsValue(IPO_DETAILS_MOCK);
            onValuesChange();
          }}
          remaining={remaining}
          disabled={!pageOperations?.update}
          label={id ? 'Update' : 'Save and proceed'}
        />
      </Form>

      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update IPO"
        confirmText="Are you sure you want to update this IPO?"
        handleOperation={() =>
          handleUpdateIpo(
            updateIpo,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};

export default IpoDetails;
